// ============================================================
// FiringSolutions - WHERE TO HIT, as one list (v8.424.0; the board
// beside the targeting console since v8.250.0). One row per place:
// to hit, expected damage, and the armour there. The first row is
// the shot left to the dice; each of the others is a called shot. Clicking a
// zone row engages the called shot there via the same shared
// writers the console uses; clicking AUTO (or the engaged row)
// clears it. Attack math only - the target's FACTS belong on the
// Target side (the v8.246.1 dossier retired here by that ruling).
// ============================================================
import clsx from '../../utils/clsx';
import { getBodyConfig } from '../../engine/bodyConfig';
import type { FiringSolutions as Solutions } from '../../engine/probability';
import type { Target } from '../../types';
import styles from '../../styles/FiringSolutions.module.css';

interface Props {
  target: Target | null;
  solutions: Solutions | null;
  loading: boolean;
  /** Whether the called-shot chip is engaged, and where. */
  calledShot: boolean;
  targetLocation: string;
  onEngageZone: (id: string) => void;
  onClear: () => void;
  disabled?: boolean;
}

const pct = (p: number) => `${Math.round(p * 100)}%`;
const dmg = (d: number) => (Math.round(d * 10) / 10).toFixed(1);

/** The armour at a location. A vehicle is armoured by facing, which the silhouette beside the list reads out. */
function armourAt(target: Target, zoneId: string): string {
  const value = (target.armour as Record<string, number> | undefined)?.[zoneId];
  return typeof value === 'number' ? String(value) : '-';
}

/** Same wording and precedence as the targeting console's stencil. */
function massStencil(target: Target): string | null {
  const bodyType = target.bodyType || (target.horde?.isHorde ? 'horde' : 'humanoid');
  if (bodyType === 'horde' || target.horde?.isHorde) return 'MASS TARGET - HITS STRIKE THE HORDE';
  if (!getBodyConfig(bodyType).supportsCalledShot) return 'NO VULNERABLE LOCATIONS';
  return null;
}

export default function FiringSolutions({
  target,
  solutions,
  loading,
  calledShot,
  targetLocation,
  onEngageZone,
  onClear,
  disabled = false,
}: Props) {
  if (!target || !solutions) return <EmptyBoard computing={!!target} />;
  const stencil = massStencil(target);
  // A disabled board shows its rows but presses nothing.
  const press = (fn: () => void) => (disabled ? undefined : fn);
  return (
    <div {...boardProps(loading, disabled)}>
      <BoardHeader />
      <div className={styles.rows}>
        <AutoRow auto={solutions.auto} active={!calledShot} onPress={press(onClear)} />
        {stencil ? (
          <div className={styles.stencil} data-testid="firing-solutions-stencil">
            {stencil}
          </div>
        ) : (
          <CalledShotRows
            solutions={solutions}
            engagedId={calledShot ? targetLocation : null}
            press={press}
            onEngageZone={onEngageZone}
            onClear={onClear}
            armourAt={(zoneId) => armourAt(target, zoneId)}
          />
        )}
      </div>
    </div>
  );
}

/** No target yet, or its solutions still computing. */
function EmptyBoard({ computing }: { computing: boolean }) {
  return (
    <div className={styles.board} data-testid="firing-solutions">
      <BoardHeader />
      <div className={styles.empty}>{computing ? 'COMPUTING...' : 'NO FIRING SOLUTION - NO TARGET'}</div>
    </div>
  );
}

/** The board's class and state attributes: dimmed while computing or disabled. */
function boardProps(loading: boolean, disabled: boolean) {
  return {
    className: clsx(styles.board, loading && styles.loading, disabled && styles.disabled),
    'data-testid': 'firing-solutions',
    'aria-busy': loading || undefined,
    'aria-disabled': disabled || undefined,
  };
}

/** The list's column heads. The silhouette beside it carries no title of its own. */
function BoardHeader() {
  return (
    <div className={styles.header} aria-hidden="true">
      <span className={styles.rowName}>Location</span>
      <span className={styles.cell}>To hit</span>
      <span className={styles.cell}>Damage</span>
      <span className={styles.cell}>Armour</span>
    </div>
  );
}

interface AutoRowProps {
  auto: Solutions['auto'];
  active: boolean;
  onPress: (() => void) | undefined;
}

/** The shot left to the dice. Its armour varies with where it lands. */
function AutoRow({ auto, active, onPress }: AutoRowProps) {
  return (
    <button
      type="button"
      className={clsx(styles.row, active && styles.rowActive)}
      data-testid="firing-solution-auto"
      onClick={onPress}
      aria-pressed={active}
      title="No called shot - the dice pick the location"
    >
      <span className={styles.rowName}>
        By the roll
        {auto.jamChance > 0 && (
          <span className={styles.rowStatFaint} data-testid="firing-solutions-jam">
            {' '}
            jam {pct(auto.jamChance)}
          </span>
        )}
      </span>
      <span className={styles.cell}>{pct(auto.hitChance)}</span>
      <span className={styles.cell}>{dmg(auto.meanDamage)}</span>
      <span className={styles.cell}>varies</span>
    </button>
  );
}

interface CalledShotRowsProps {
  solutions: Solutions;
  /** The zone a called shot is engaged on, or null. */
  engagedId: string | null;
  press: (fn: () => void) => (() => void) | undefined;
  onEngageZone: (id: string) => void;
  onClear: () => void;
  /** The armour a hit meets at a location, as the row prints it. */
  armourAt: (zoneId: string) => string;
}

/**
 * The called shots. The penalty is flat across zones, so it is named once
 * in the divider; every row then carries the same to-hit and differs in
 * what gets through and what it meets. Clicking the engaged row clears it.
 */
function CalledShotRows({ solutions, engagedId, press, onEngageZone, onClear, armourAt }: CalledShotRowsProps) {
  const hit = solutions.calledHitChance != null ? pct(solutions.calledHitChance) : '-';
  return (
    <>
      <div className={styles.calledHeader} data-testid="firing-solutions-called">
        CALLED SHOT <span className={styles.penalty}>{-solutions.calledShotPenalty || 0}</span>
      </div>
      {solutions.zones.map((zone) => (
        <ZoneRow
          key={zone.id}
          zone={zone}
          hit={hit}
          armour={armourAt(zone.id)}
          engaged={engagedId === zone.id}
          onPress={(engaged) => press(() => (engaged ? onClear() : onEngageZone(zone.id)))}
        />
      ))}
    </>
  );
}

interface ZoneRowProps {
  zone: Solutions['zones'][number];
  /** The called shot's to-hit, the same on every row. */
  hit: string;
  armour: string;
  engaged: boolean;
  /** The row's click handler, given whether it is engaged (undefined when disabled). */
  onPress: (engaged: boolean) => (() => void) | undefined;
}

function ZoneRow({ zone, hit, armour, engaged, onPress }: ZoneRowProps) {
  return (
    <button
      type="button"
      className={clsx(styles.row, styles.zoneRow, engaged && styles.rowActive)}
      data-testid={`firing-solution-${zone.id}`}
      onClick={onPress(engaged)}
      aria-pressed={engaged}
      title={engaged ? `${zone.name} - clear called shot` : `Call the shot - ${zone.name}`}
    >
      <span className={styles.rowName}>{zone.name.toUpperCase()}</span>
      <span className={styles.cell}>{hit}</span>
      <span className={styles.cell}>{dmg(zone.meanDamage)}</span>
      <span className={styles.cell}>{armour}</span>
    </button>
  );
}
